import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { ErroConsulta } from "@/components/ErroConsulta";

const data = (d: Date | string | null) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

/**
 * Organizações que o proprietário mandou excluir (7 dias só leitura). O admin não aprova: acompanha
 * e, num contato de suporte, desfaz.
 */
export function AdminExclusoesAgendadas() {
  const utils = trpc.useUtils();
  const { data: itens, isLoading, error } = trpc.admin.exclusoes.list.useQuery();
  const [alvo, setAlvo] = useState<{ id: string; name: string } | null>(null);
  const [motivo, setMotivo] = useState("");
  const desfazer = trpc.admin.exclusoes.desfazer.useMutation({
    onSuccess: () => {
      toast.success("Exclusão desfeita. O proprietário e os membros foram avisados.");
      setAlvo(null);
      setMotivo("");
      utils.admin.exclusoes.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Exclusões agendadas</h1>
        <p className="mt-1 text-sm text-admin-700">
          Organizações que o proprietário mandou excluir em Configurações. Ficam só leitura por 7 dias e são apagadas no fim do prazo.
        </p>
      </div>
      <Card className="border-admin-200">
        <CardContent className="overflow-x-auto p-0">
          {error ? (
            <div className="p-5">
              <ErroConsulta error={error} />
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
            </div>
          ) : !itens?.length ? (
            <p className="py-12 text-center text-sm text-admin-700">Nenhuma exclusão agendada.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-admin-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-admin-700">
                <tr>
                  <th className="px-4 py-2.5">Organização</th>
                  <th className="px-4 py-2.5">Pedida por</th>
                  <th className="px-4 py-2.5">Agendada em</th>
                  <th className="px-4 py-2.5">Exclusão em</th>
                  <th className="px-4 py-2.5">Membros</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {itens.map((o) => (
                  <tr key={o.id} className="border-b border-admin-100 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-admin-900">{o.name}</td>
                    <td className="px-4 py-2.5 text-admin-700">{o.agendadaPor ? `${o.agendadaPor.name ?? ""} (${o.agendadaPor.email})` : "—"}</td>
                    <td className="px-4 py-2.5 text-admin-700">{data(o.exclusaoAgendadaEm)}</td>
                    <td className="px-4 py-2.5 font-semibold text-red-700">{data(o.exclusaoPrevistaPara)}</td>
                    <td className="px-4 py-2.5 text-admin-700">{o.membros}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button type="button" onClick={() => setAlvo({ id: o.id, name: o.name })} className="text-xs font-semibold text-admin-900 underline hover:no-underline">
                        Desfazer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!alvo} onOpenChange={(v) => !v && setAlvo(null)}>
        <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl">
          <DialogHeader>
            <DialogTitle>Desfazer a exclusão de {alvo?.name}?</DialogTitle>
            <DialogDescription>A organização volta ao normal e o proprietário e os membros recebem o aviso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="motivo-desfazer">Motivo (fica no registro)</Label>
            <Input id="motivo-desfazer" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: o proprietário pediu pelo suporte" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setAlvo(null)} className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50">
              Cancelar
            </button>
            <button
              type="button"
              disabled={motivo.trim().length < 5 || desfazer.isPending}
              onClick={() => alvo && desfazer.mutate({ organizationId: alvo.id, motivo })}
              className="inline-flex h-9 items-center rounded-md bg-admin-900 px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              Desfazer exclusão
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
